/**
 * Componente Trash (Papelera) — listado, restauración y purga de items eliminados.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Table, Button, Modal, Input, DatePicker, Select, Space, Tag, Tooltip,
  notification, Empty
} from 'antd';
import {
  DeleteOutlined, UndoOutlined, ClearOutlined, SearchOutlined,
  FolderOpenOutlined, FileOutlined, ReloadOutlined
} from '@ant-design/icons';

import { trash as trashActions } from '../../actions/database/trash';
import { isGlobalSupervisor } from '../../lib/closed_folder_perms';
import { trashScope, canRestoreTrashItem } from '../../lib/trash_scope';
import { customers as customersActions } from '../../actions/database/customers';

const { RangePicker } = DatePicker;

const TYPE_META = {
  'folder':       { label: 'Carpeta',         icon: <FolderOpenOutlined />, color: 'gold' },
  'file-legacy':  { label: 'Archivo',         icon: <FileOutlined />,       color: 'blue' },
  'file-inline':  { label: 'Archivo',         icon: <FileOutlined />,       color: 'cyan' },
  // Adjuntos del cajón de un documento (lib/attachment_trash.js).
  'accounting':   { label: 'Contabilización', icon: <FileOutlined />,       color: 'green' },
  'xml':          { label: 'XML',             icon: <FileOutlined />,       color: 'purple' },
  'link':         { label: 'Vinculado',       icon: <FileOutlined />,       color: 'geekblue' }
};

const formatDate = (ts) => {
  try {
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : null);
    if (!d) return '—';
    return d.toLocaleString('es-CO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch (_e) { return '—'; }
};

const daysRemaining = (ts, retention = 30) => {
  try {
    const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : null);
    if (!d) return null;
    const elapsed = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, retention - elapsed);
  } catch (_e) { return null; }
};

export const Trash = ({ lockedCustomerId, compact = false }) => {
  const dispatch = useDispatch();
  const roles = useSelector(state => state.get('roles').get('roles').roles);

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState({
    customerId: lockedCustomerId || undefined,
    range: null,
    query: ''
  });

  // Super-admin (root/contador): ve toda la papelera y el botón "Vaciar".
  // Admin de empresa: solo la papelera de SU empresa.
  const isAdmin = roles?.documents?.level === 'all';
  const isSuper = isAdmin;
  // Solo root ve y vacía la papelera global; los demás, lo que ellos
  // borraron (lib/trash_scope.js). La etiqueta lo dice para que nadie crea
  // que "Vaciar" le borra la papelera a todo el mundo.
  const isTrashRoot = isGlobalSupervisor(roles); // root o supervisora global: vista global
  const isLocked = !!lockedCustomerId;
  const myUid = useSelector(state => state.get('authenticate')?.get('login')?.user?.uid) || null;
  const scope = useMemo(() => trashScope({ roles, uid: myUid }), [roles, myUid]);

  const loadCustomers = useCallback(async () => {
    try {
      const res = await dispatch(customersActions.read({
        roles: roles?.documents || { read: true },
        filter: { field: 'Razon social', value: '' }
      }));
      const list = (res?.customers || []).map(c => ({
        id: c.id,
        name: c.data?.['Razon social'] || c.id
      }));
      setCustomers(list);
    } catch (e) {
      console.error('Error cargando clientes:', e);
    }
  }, [dispatch, roles]);

  const onRead = useCallback(async () => {
    setLoading(true);
    try {
      const f = {};
      if (filter.customerId) f.customerId = filter.customerId;
      if (filter.range?.length === 2) {
        f.from = filter.range[0].startOf('day').toDate();
        f.to = filter.range[1].endOf('day').toDate();
      }
      const res = await dispatch(trashActions.read({ roles, filter: f }));
      setItems(res?.items || []);
      const purged = res?.autoPurged || 0;
      if (purged > 0) {
        notification.info({
          message: `${purged} elemento${purged !== 1 ? 's' : ''} expirado${purged !== 1 ? 's' : ''} purgado${purged !== 1 ? 's' : ''}`,
          description: 'Se eliminaron automáticamente por superar la retención de 30 días.'
        });
      }
    } catch (e) {
      notification.error({
        message: 'No se pudo cargar la papelera',
        description: e?.message || 'Error de red.'
      });
    } finally {
      setLoading(false);
    }
  }, [dispatch, roles, filter]);

  useEffect(() => {
    if (!isLocked) loadCustomers();
  }, [loadCustomers, isLocked]);
  useEffect(() => { onRead(); }, [onRead]);

  // Admin de empresa (no super-admin): la papelera solo lee SU empresa, así que
  // autoseleccionamos la primera empresa disponible (customersActions.read ya
  // devuelve solo las del usuario) para acotar la query y evitar la lectura
  // global que las reglas rechazan.
  useEffect(() => {
    if (!isSuper && !isLocked && !filter.customerId && customers.length > 0) {
      setFilter(f => ({ ...f, customerId: customers[0].id }));
    }
  }, [isSuper, isLocked, filter.customerId, customers]);

  // Si el filtro está bloqueado por prop, sincronizar al cambiar
  useEffect(() => {
    if (isLocked) setFilter(f => ({ ...f, customerId: lockedCustomerId }));
  }, [lockedCustomerId, isLocked]);

  const customerName = useCallback((id) => {
    const c = customers.find(x => x.id === id);
    return c?.name || id || '—';
  }, [customers]);

  const filtered = useMemo(() => {
    const q = (filter.query || '').toLowerCase().trim();
    if (!q) return items;
    return items.filter(it =>
      (it.itemName || '').toLowerCase().includes(q) ||
      (it.deletedByName || '').toLowerCase().includes(q) ||
      customerName(it.customerId).toLowerCase().includes(q)
    );
  }, [items, filter.query, customerName]);

  const onRestore = (record) => {
    Modal.confirm({
      title: '¿Restaurar este elemento?',
      content: <span>El elemento <strong>{record.itemName}</strong> volverá a su ubicación original.</span>,
      okText: 'Restaurar',
      cancelText: 'Cancelar',
      onOk: async () => {
        setBusyId(record.id);
        try {
          const res = await dispatch(trashActions.restore({ id: record.id, roles }));
          notification.success({ message: 'Elemento restaurado.' });
          // Las reglas solo dejan restaurar matrices a quien administra la
          // empresa: la carpeta vuelve igual, heredando los permisos de arriba.
          if (res?.matricesSkipped > 0) {
            notification.warning({
              message: 'Permisos no restaurados',
              description: `No se restauraron ${res.matricesSkipped} asignaciones de permisos de la carpeta (requiere administrador de la empresa). La carpeta usa los permisos heredados; pide a un administrador que los vuelva a asignar si hace falta.`,
              duration: 0
            });
          }
          setItems(prev => prev.filter(it => it.id !== record.id));
        } catch (e) {
          notification.error({
            message: 'No se pudo restaurar',
            description: e?.message || 'Verifica que la ubicación original aún exista.'
          });
        } finally {
          setBusyId(null);
        }
      }
    });
  };

  const onPurge = (record) => {
    Modal.confirm({
      title: '¿Eliminar permanentemente?',
      content: <span>El elemento <strong>{record.itemName}</strong> no podrá recuperarse.</span>,
      okText: 'Eliminar definitivamente',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        setBusyId(record.id);
        try {
          await dispatch(trashActions.purge({ id: record.id, roles }));
          notification.success({ message: 'Elemento eliminado permanentemente.' });
          setItems(prev => prev.filter(it => it.id !== record.id));
        } catch (e) {
          notification.error({
            message: 'No se pudo eliminar',
            description: e?.message || 'Error de red.'
          });
        } finally {
          setBusyId(null);
        }
      }
    });
  };

  const onPurgeAll = () => {
    if (filtered.length === 0) return;
    Modal.confirm({
      title: isTrashRoot ? '¿Vaciar la papelera?' : '¿Vaciar tu papelera?',
      content: (
        <span>
          Se eliminarán <strong>{filtered.length}</strong> elemento{filtered.length !== 1 ? 's' : ''}
          {filter.customerId ? <> del cliente <strong>{customerName(filter.customerId)}</strong></> : null}.
          Esta acción no se puede deshacer.
        </span>
      ),
      okText: 'Vaciar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        setLoading(true);
        try {
          await dispatch(trashActions.purgeAll({ roles, customerId: filter.customerId || undefined }));
          notification.success({ message: 'Papelera vaciada.' });
          onRead();
        } catch (e) {
          notification.error({
            message: 'No se pudo vaciar la papelera',
            description: e?.message || 'Error de red.'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'itemType',
      width: 110,
      render: (t) => {
        const m = TYPE_META[t] || { label: t || '—', icon: null, color: 'default' };
        return <Tag color={m.color} icon={m.icon}>{m.label}</Tag>;
      }
    },
    {
      title: 'Nombre',
      dataIndex: 'itemName',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <strong>{name || '—'}</strong>
          {record.itemExt && (
            <span style={{ fontSize: 11, color: '#888', fontFamily: 'monospace' }}>
              .{record.itemExt}
            </span>
          )}
        </Space>
      )
    },
    !isLocked && {
      title: 'Cliente',
      dataIndex: 'customerId',
      width: 220,
      render: (id) => customerName(id)
    },
    {
      title: 'Eliminado por',
      dataIndex: 'deletedByName',
      width: 180,
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <span>{name || '—'}</span>
          {record.requestedBy && record.requestedBy === myUid && record.deletedBy !== myUid && (
            <Tag color="blue">Solicitado por ti</Tag>
          )}
        </Space>
      )
    },
    {
      title: 'Fecha',
      dataIndex: 'deletedAt',
      width: 160,
      render: (ts) => formatDate(ts)
    },
    {
      title: 'Retención',
      dataIndex: 'deletedAt',
      width: 110,
      render: (ts, record) => {
        const left = daysRemaining(ts, record.retentionDays || 30);
        if (left == null) return '—';
        const color = left <= 3 ? 'red' : left <= 10 ? 'orange' : 'green';
        return <Tag color={color}>{left} día{left !== 1 ? 's' : ''}</Tag>;
      }
    },
    {
      title: 'Acciones',
      width: compact ? 140 : 220,
      render: (_t, record) => !(scope?.global || canRestoreTrashItem({ item: record, uid: myUid, scope })) ? (
        <Tooltip title="Lo eliminó una supervisora al aprobar tu solicitud; solo ella puede restaurarlo o eliminarlo definitivamente.">
          <span style={{ color: '#888' }}>Solo lectura</span>
        </Tooltip>
      ) : (
        <Space>
          <Tooltip title={record.status === 'deleting' ? 'Eliminación en curso…' : 'Restaurar a su ubicación original'}>
            <Button
              icon={<UndoOutlined />}
              onClick={() => onRestore(record)}
              loading={busyId === record.id}
              disabled={record.status === 'deleting'}
              size="small"
            >
              {compact ? null : 'Restaurar'}
            </Button>
          </Tooltip>
          <Tooltip title={record.status === 'deleting' ? 'Eliminación en curso…' : 'Eliminar permanentemente'}>
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => onPurge(record)}
              loading={busyId === record.id}
              disabled={record.status === 'deleting'}
              size="small"
            />
          </Tooltip>
        </Space>
      )
    }
  ].filter(Boolean);

  return (
    <div style={{ padding: compact ? '0' : '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Input
          placeholder={isLocked ? 'Buscar en papelera del cliente...' : 'Buscar por nombre, usuario o cliente...'}
          prefix={<SearchOutlined />}
          value={filter.query}
          onChange={(e) => setFilter(f => ({ ...f, query: e.target.value }))}
          style={{ width: compact ? '100%' : 280, flex: compact ? 1 : 'unset', minWidth: 200 }}
          allowClear
        />
        {!isLocked && (
          <Select
            allowClear
            placeholder="Cliente"
            value={filter.customerId}
            onChange={(v) => setFilter(f => ({ ...f, customerId: v }))}
            style={{ width: 260 }}
            showSearch
            optionFilterProp="label"
            options={customers.map(c => ({ value: c.id, label: c.name }))}
          />
        )}
        <RangePicker
          value={filter.range}
          onChange={(r) => setFilter(f => ({ ...f, range: r }))}
        />
        <Button icon={<ReloadOutlined />} onClick={onRead} loading={loading}>
          {compact ? null : 'Actualizar'}
        </Button>
        <div style={{ flex: 1 }} />
        {isAdmin && (
          <Button
            icon={<ClearOutlined />}
            danger
            onClick={onPurgeAll}
            disabled={filtered.length === 0}
          >
            {isTrashRoot
              ? (isLocked ? 'Vaciar (este cliente)' : `Vaciar papelera${filter.customerId ? ' (cliente)' : ''}`)
              : (isLocked || filter.customerId ? 'Vaciar mi papelera (cliente)' : 'Vaciar mi papelera')}
          </Button>
        )}
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        loading={loading}
        pagination={{ defaultPageSize: compact ? 10 : 20, showSizeChanger: !compact }}
        locale={{ emptyText: <Empty description="Papelera vacía" /> }}
        size={compact ? 'small' : 'middle'}
      />
    </div>
  );
};
